import React, { useEffect, useState } from "react";
import { api, formatINR } from "../lib/api";
import { useSEO } from "../lib/seo";
import { HiveMark } from "../components/Logo";
import { Package, IndianRupee, AlertTriangle, Settings2, Tag, Star, ChevronLeft, Truck, ExternalLink, FileText, CalendarClock, ClipboardList, RefreshCw } from "lucide-react";

function useAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem("hh_admin_token"));
  const [challenge, setChallenge] = useState(null); // { challenge_id, channels, expires_in }
  const finish = (data) => {
    localStorage.setItem("hh_admin_token", data.token);
    if (data.device_token) localStorage.setItem("hh_admin_device", data.device_token);
    setChallenge(null);
    setToken(data.token);
  };
  const login = async (email, password) => {
    const r = await api.post("/admin/login", { email, password, device_token: localStorage.getItem("hh_admin_device") || undefined });
    if (r.data.two_factor) { setChallenge(r.data); return; }
    finish(r.data);
  };
  const verifyOtp = async (code, rememberDevice) => {
    const r = await api.post("/admin/login/verify-otp", { challenge_id: challenge.challenge_id, code, remember_device: rememberDevice });
    finish(r.data);
  };
  const resendOtp = async () => {
    const r = await api.post("/admin/login/resend-otp", { challenge_id: challenge.challenge_id });
    setChallenge((c) => ({ ...c, channels: r.data.channels }));
  };
  const logout = () => { localStorage.removeItem("hh_admin_token"); setToken(null); };
  return { token, login, logout, challenge, verifyOtp, resendOtp, cancelOtp: () => setChallenge(null) };
}

function OtpStep({ challenge, onVerify, onResend, onCancel }) {
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await onVerify(code.trim(), remember); } catch (err) { setError(err.response?.data?.detail || "Couldn't verify the code."); } finally { setBusy(false); }
  };
  const resend = async () => {
    setError(""); setNote("");
    try { await onResend(); setNote("A fresh code is on its way."); } catch (err) { setError(err.response?.data?.detail || "Couldn't resend right now."); }
  };
  return (
    <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl card-outline sticker p-7" data-testid="admin-otp-form">
      <HiveMark className="w-12 h-12 mx-auto" />
      <h1 className="font-display font-semibold text-2xl text-center mt-3">Enter your sign-in code</h1>
      <p className="text-xs text-charcoal/60 text-center mt-2" data-testid="admin-otp-channels">
        We sent a 6-digit code to {challenge.channels.filter((c) => c !== "debug log").join(" and ") || "your registered channels"}. It expires in 5 minutes.
      </p>
      <div className="mt-5 space-y-3">
        <input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoFocus autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="••••••" aria-label="One-time code" data-testid="admin-otp-input"
          className="w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-center text-2xl tracking-[0.5em] font-display" />
        <label className="flex items-center gap-2 text-xs text-charcoal/70">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="admin-otp-remember" className="accent-forest" />
          Trust this device for 30 days
        </label>
        {error && <p role="alert" className="text-xs text-coral font-semibold" data-testid="admin-otp-error">{error}</p>}
        {note && <p role="status" className="text-xs text-forest font-semibold" data-testid="admin-otp-note">{note}</p>}
        <button type="submit" disabled={busy || code.length !== 6} className="w-full bg-forest text-white font-semibold py-3 rounded-full sticker disabled:opacity-50" data-testid="admin-otp-submit">{busy ? "Checking…" : "Verify & sign in"}</button>
        <div className="flex justify-between text-xs">
          <button type="button" onClick={resend} className="underline underline-offset-4 text-charcoal/70 hover:text-forest" data-testid="admin-otp-resend">Resend code</button>
          <button type="button" onClick={onCancel} className="underline underline-offset-4 text-charcoal/70 hover:text-coral" data-testid="admin-otp-cancel">Start over</button>
        </div>
      </div>
    </form>
  );
}

function authedGet(path) {
  return api.get(path, { headers: { Authorization: `Bearer ${localStorage.getItem("hh_admin_token")}` } }).then((r) => r.data);
}
function authedPatch(path, body) {
  return api.patch(path, body, { headers: { Authorization: `Bearer ${localStorage.getItem("hh_admin_token")}` } }).then((r) => r.data);
}
function authedPut(path, body) {
  return api.put(path, body, { headers: { Authorization: `Bearer ${localStorage.getItem("hh_admin_token")}` } }).then((r) => r.data);
}
function authedPost(path, body) {
  return api.post(path, body, { headers: { Authorization: `Bearer ${localStorage.getItem("hh_admin_token")}` } }).then((r) => r.data);
}

function ShiprocketPanel({ order: o, act }) {
  const panelUrl = `https://app.shiprocket.in/seller/orders/details/${o.shiprocket_order_id}`;
  const isClosed = ["cancelled", "refunded", "returned", "delivered"].includes(o.status);
  const open = (path) => act(path, (r) => { if (r?.url) window.open(r.url, "_blank", "noopener"); return `${o.order_number}: document ready`; });
  return (
    <div className="mt-3 rounded-2xl bg-cream/70 border border-charcoal/10 p-3 text-xs" data-testid={`admin-shiprocket-${o.order_number}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold uppercase tracking-widest text-[10px] text-forest">Shiprocket</span>
        <span className="bg-white rounded-full px-2.5 py-1 font-semibold" data-testid={`admin-sr-status-${o.order_number}`}>{isClosed ? (o.shipment_cancelled ? "CANCELLED" : o.shiprocket_status || "CLOSED") : o.shiprocket_status || "NEW"}</span>
        <span className="text-charcoal/60">order {o.shiprocket_order_id}{o.shipment_id ? ` · shipment ${o.shipment_id}` : ""}</span>
        {o.awb ? (
          <span className="font-semibold" data-testid={`admin-courier-${o.order_number}`}>{o.courier || "Courier"} · AWB {o.awb}</span>
        ) : !isClosed ? (
          <span className="text-charcoal/60 italic" data-testid={`admin-awaiting-courier-${o.order_number}`}>Choose a courier and ship inside Shiprocket — the AWB syncs back here automatically.</span>
        ) : null}
        {o.pickup_scheduled_date && <span className="text-charcoal/60">Pickup: {o.pickup_scheduled_date}</span>}
      </div>
      <div className="flex flex-wrap gap-2 mt-2.5">
        <a href={panelUrl} target="_blank" rel="noreferrer" data-testid={`admin-open-shiprocket-${o.order_number}`} className="border border-forest text-forest font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1">Open in Shiprocket <ExternalLink className="w-3 h-3" /></a>
        {!isClosed && <button type="button" data-testid={`admin-track-refresh-${o.order_number}`} onClick={() => act(`/admin/orders/${o.order_number}/tracking`, (r) => `${o.order_number}: ${r.tracking?.current_status || r.order?.shiprocket_status || "synced"}`)} className="border border-charcoal/20 font-semibold rounded-full px-3 py-1.5">Refresh from Shiprocket</button>}
        {o.awb && !["cancelled", "refunded"].includes(o.status) && (
          <>
            <button type="button" data-testid={`admin-label-${o.order_number}`} onClick={() => open(`/admin/orders/${o.order_number}/label`)} className="bg-charcoal text-white font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Download label</button>
            <button type="button" data-testid={`admin-pickup-${o.order_number}`} onClick={() => act(`/admin/orders/${o.order_number}/pickup`, (r) => `${o.order_number}: pickup scheduled${r.scheduled_date ? ` for ${r.scheduled_date}` : ""}`)} className="bg-coral text-white font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Schedule pickup</button>
            <button type="button" data-testid={`admin-manifest-${o.order_number}`} onClick={() => open(`/admin/orders/${o.order_number}/manifest`)} className="bg-charcoal text-white font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Download manifest</button>
          </>
        )}
      </div>
    </div>
  );
}

const ACTIVITY_LABEL = { login: "Signed in", login_trusted_device: "Signed in (trusted device)", otp_sent: "Code sent", otp_failed: "Wrong code", password_failed: "Wrong password" };

const CART_STATUS = { open: ["Reminder pending", "bg-butter"], reminded: ["Reminders sent", "bg-sky/40"], recovered: ["Recovered", "bg-sage"], unsubscribed: ["Unsubscribed", "bg-charcoal/10"], no_email: ["No email", "bg-charcoal/10"] };

function AbandonedCarts() {
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const load = () => authedGet("/admin/abandoned").then(setData).catch(() => setData({ items: [], stats: {} }));
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-xs text-charcoal/50">Loading…</p>;
  const s = data.stats || {};
  const remind = (id) => authedPost(`/admin/abandoned/${id}/remind`, {}).then(() => { setMsg("Reminder queued."); load(); }).catch((e) => setMsg(e.response?.data?.detail || "Couldn't queue that reminder."));
  return (
    <div data-testid="admin-abandoned">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        {[["Waiting", s.open || 0], ["Reminded", s.reminded || 0], ["Recovered", s.recovered || 0], ["Recovered value", `₹${Math.round(s.recovered_value || 0).toLocaleString("en-IN")}`]].map(([k, v]) => (
          <div key={k} className="bg-white rounded-2xl card-outline p-4"><p className="text-charcoal/60">{k}</p><p className="font-display font-bold text-xl mt-1" data-testid={`abandoned-stat-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</p></div>
        ))}
      </div>
      <p className="text-xs text-charcoal/60 mt-3">Razorpay sends every Magic Checkout the shopper leaves before paying. We email a nudge after {data.delays_hours?.[0]} h and a second one with your best coupon after {data.delays_hours?.[1]} h — unless they complete an order first.{!data.email_enabled && <span className="text-coral font-semibold"> Email provider is off, so reminders are not being sent.</span>}</p>
      {msg && <p className="text-xs font-semibold text-forest mt-2" data-testid="abandoned-msg">{msg}</p>}
      {!data.items.length ? <p className="text-xs text-charcoal/50 mt-4" data-testid="abandoned-empty">No abandoned checkouts yet — turn on the Abandoned webhook in Razorpay → Magic Checkout → Checkout Setup.</p> : (
        <ul className="mt-4 space-y-2">
          {data.items.map((c) => {
            const [label, cls] = CART_STATUS[c.status] || [c.status, "bg-charcoal/10"];
            return (
              <li key={c.id} className="bg-white rounded-2xl card-outline p-4 text-xs flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`abandoned-row-${c.id}`}>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${cls}`}>{label}</span>
                <span className="font-semibold">{c.name || "Shopper"}</span>
                <span className="text-charcoal/70">{c.email || "no email"}{c.phone ? ` · +91 ${c.phone}` : ""}</span>
                <span className="text-charcoal/70">{c.lines.map((l) => `${l.name} × ${l.qty}`).join(", ")}</span>
                <span className="font-bold">₹{Math.round(c.subtotal).toLocaleString("en-IN")}</span>
                {c.address?.city && <span className="text-charcoal/50">{c.address.city}, {c.address.pin}</span>}
                <span className="text-charcoal/50">{c.reminders_sent || 0} reminder{c.reminders_sent === 1 ? "" : "s"}{c.recovered_order ? ` · order ${c.recovered_order}` : ""}</span>
                <span className="ml-auto text-charcoal/50">{new Date(c.received_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                {c.email && c.status !== "recovered" && c.status !== "unsubscribed" && (
                  <button type="button" onClick={() => remind(c.id)} data-testid={`abandoned-remind-${c.id}`} className="bg-forest text-white font-semibold rounded-full px-3 py-1.5">Send reminder now</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ActivityLog() {
  const [items, setItems] = useState(null);
  useEffect(() => { authedGet("/admin/activity").then(setItems).catch(() => setItems([])); }, []);
  if (items === null) return <p className="text-xs text-charcoal/50 mt-3">Loading…</p>;
  if (!items.length) return <p className="text-xs text-charcoal/50 mt-3">No sign-in activity yet.</p>;
  const device = (ua = "") => (/iPhone|iPad/.test(ua) ? "iPhone/iPad" : /Android/.test(ua) ? "Android" : /Mac/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Unknown device") + (/Chrome/.test(ua) ? " · Chrome" : /Safari/.test(ua) ? " · Safari" : /Firefox/.test(ua) ? " · Firefox" : "");
  return (
    <ul className="mt-3 bg-white rounded-2xl card-outline divide-y divide-charcoal/10 text-xs" data-testid="admin-activity-log">
      {items.slice(0, 12).map((a) => (
        <li key={a.id || a.at} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5" data-testid={`admin-activity-${a.event}`}>
          <span className={`font-bold ${["password_failed", "otp_failed"].includes(a.event) ? "text-coral" : "text-forest"}`}>{ACTIVITY_LABEL[a.event] || a.event}</span>
          <span className="text-charcoal/70">{a.email || "—"}</span>
          <span className="text-charcoal/50">{device(a.user_agent)}</span>
          <span className="text-charcoal/50">IP {a.ip || "?"}</span>
          <span className="ml-auto text-charcoal/50">{new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
        </li>
      ))}
    </ul>
  );
}

const STATUSES = ["confirmed", "packed", "shipped", "out_for_delivery", "delivered", "cancelled", "returned", "refunded"];

export default function Admin() {
  const { token, login, logout, challenge, verifyOtp, resendOtp, cancelOtp } = useAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [settings, setSettings] = useState(null);
  const [orderMsg, setOrderMsg] = useState("");
  const isLiveShipping = overview?.integrations?.shipping_provider === "shiprocket";
  const act = (path, okMsg) => { setOrderMsg(""); return authedPost(path, {}).then((r) => { setOrderMsg(typeof okMsg === "function" ? okMsg(r) : okMsg); load(); return r; }).catch((e) => setOrderMsg(e.response?.data?.detail || "That didn't work — please try again.")); };
  useSEO({ title: "Admin — Haven Hives" });

  const load = React.useCallback(() => {
    if (!token) return;
    const map = { overview: setOverview, products: setProducts, orders: setOrders, reviews: setReviews, coupons: setCoupons, settings: setSettings };
    authedGet("/admin/overview").then(setOverview).catch(() => {});
    authedGet("/admin/products").then(setProducts).catch(() => {});
    authedGet("/admin/orders").then(setOrders).catch(() => {});
    authedGet("/admin/reviews").then(setReviews).catch(() => {});
    authedGet("/admin/coupons").then(setCoupons).catch(() => {});
    authedGet("/admin/settings").then(setSettings).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token && challenge) {
    return (
      <div className="min-h-screen bg-cream dot-grid flex items-center justify-center p-5">
        <OtpStep challenge={challenge} onVerify={verifyOtp} onResend={resendOtp} onCancel={cancelOtp} />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-cream dot-grid flex items-center justify-center p-5">
        <form onSubmit={async (e) => { e.preventDefault(); setError(""); try { await login(email, password); } catch (err) { setError(err.response?.data?.detail || "Login failed"); } }}
          className="w-full max-w-sm bg-white rounded-3xl card-outline sticker p-7" data-testid="admin-login-form">
          <HiveMark className="w-12 h-12 mx-auto" />
          <h1 className="font-display font-semibold text-2xl text-center mt-3">Hive Admin</h1>
          <p className="text-xs text-charcoal/50 text-center mt-1">Protected area — store team only. Two-step sign-in.</p>
          <div className="mt-5 space-y-3">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin email" aria-label="Admin email" data-testid="admin-email-input" className="w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" aria-label="Admin password" data-testid="admin-password-input" className="w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
            {error && <p role="alert" className="text-xs text-coral font-semibold" data-testid="admin-login-error">{error}</p>}
            <button type="submit" className="w-full bg-forest text-white font-semibold py-3 rounded-full sticker" data-testid="admin-login-submit">Sign in</button>
          </div>
        </form>
      </div>
    );
  }

  const TABS = [["overview", "Overview"], ["products", "Products"], ["orders", "Orders"], ["abandoned", "Abandoned carts"], ["reviews", "Reviews"], ["coupons", "Coupons"], ["settings", "Settings"]];

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-forest text-cream sticky top-0 z-40">
        <div className="max-w-[1700px] xl:px-14 mx-auto px-5 py-3.5 flex items-center gap-4">
          <HiveMark className="w-8 h-8" />
          <h1 className="font-display font-semibold text-lg">Hive Admin</h1>
          <button onClick={logout} className="ml-auto text-xs font-semibold bg-cream/10 hover:bg-cream/20 px-4 py-2 rounded-full" data-testid="admin-logout">Sign out</button>
        </div>
        <nav className="max-w-[1700px] xl:px-14 mx-auto px-5 pb-2 flex gap-1 overflow-x-auto no-scrollbar" aria-label="Admin sections">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} aria-current={tab === id} data-testid={`admin-tab-${id}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${tab === id ? "bg-cream text-charcoal" : "hover:bg-cream/10"}`}>{label}</button>
          ))}
        </nav>
      </header>

      <main className="max-w-[1700px] xl:px-14 mx-auto px-5 py-8 text-sm">
        {tab === "overview" && overview && (
          <div data-testid="admin-overview">
            {orderMsg && <p role="status" className="text-xs font-semibold bg-butter rounded-2xl px-4 py-2.5 mb-4" data-testid="admin-overview-msg">{orderMsg}</p>}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[[`${overview.orders_count}`, "Orders", Package], [formatINR(overview.revenue), "Revenue (paid)", IndianRupee], [`${overview.products}`, "Products", Tag], [`${overview.pending_reviews}`, "Reviews to approve", Star]].map(([val, label, Icon]) => (
                <div key={label} className="bg-white rounded-3xl card-outline p-5">
                  <Icon className="w-4 h-4 text-forest" />
                  <p className="font-display font-semibold text-2xl mt-2">{val}</p>
                  <p className="text-xs text-charcoal/60">{label}</p>
                </div>
              ))}
            </div>
            <h2 className="font-display font-semibold text-xl mt-8">Integration status</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
              <div className="bg-white rounded-2xl card-outline p-4">
                <p className="font-semibold">Payments</p>
                <p className="text-xs text-charcoal/60 mt-1" data-testid="admin-payment-status">
                  {overview.integrations.razorpay_configured
                    ? `Razorpay ${overview.integrations.razorpay_mode === "live" ? "LIVE — real payments" : "TEST mode — sandbox UPI/cards, no real money"}`
                    : "Test simulator active — add Razorpay keys to go live"}
                </p>
              </div>
              <div className="bg-white rounded-2xl card-outline p-4">
                <p className="font-semibold">Shipping</p>
                <p className="text-xs text-charcoal/60 mt-1" data-testid="admin-shipping-status">
                  {overview.integrations.shipping_provider === "shiprocket"
                    ? "Shiprocket connected — confirmed orders are sent automatically; pick couriers in Shiprocket and AWB/tracking sync back (every 15 min + on demand)"
                    : "Test configuration — add SHIPROCKET_EMAIL / SHIPROCKET_PASSWORD to backend .env for live rates, AWB and tracking"}
                </p>
                {overview.integrations.shipping_provider === "shiprocket" && (
                  <button type="button" data-testid="admin-sync-shipments" onClick={() => { setOrderMsg(""); authedPost("/admin/shipments/sync", {}).then((r) => { setOrderMsg(`Synced ${r.synced} open shipment(s) from Shiprocket`); load(); setTab("orders"); }).catch(() => setOrderMsg("Sync failed — try again.")); }}
                    className="mt-2 border border-forest text-forest text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Sync now</button>
                )}
              </div>
              <div className="bg-white rounded-2xl card-outline p-4">
                <p className="font-semibold">Email</p>
                <p className="text-xs text-charcoal/60 mt-1" data-testid="admin-email-status">
                  {overview.integrations.email_enabled ? `Order emails on · ${overview.emails_sent} sent${overview.emails_queued ? ` · ${overview.emails_queued} queued (retrying)` : ""}` : "Email provider not configured"}
                </p>
                {overview.integrations.email_enabled && (
                  <form className="mt-2 flex gap-1.5" onSubmit={(e) => { e.preventDefault(); const to = e.target.elements.sampleTo.value; setOrderMsg(""); authedPost("/admin/email/test", { to, kind: "shipped" }).then(() => setOrderMsg(`Sample "shipped" email sent to ${to}`)).catch((err) => setOrderMsg(err.response?.data?.detail || "Couldn't send the sample.")); }}>
                    <input name="sampleTo" type="email" required placeholder="you@example.com" data-testid="admin-email-sample-input" className="border border-charcoal/15 rounded-full px-3 py-1.5 text-xs flex-1 min-w-0" aria-label="Send a sample email to" />
                    <button type="submit" data-testid="admin-email-sample-btn" className="border border-forest text-forest text-xs font-semibold rounded-full px-3 py-1.5 whitespace-nowrap">Send sample</button>
                  </form>
                )}
              </div>
              <div className="bg-white rounded-2xl card-outline p-4">
                <p className="font-semibold">WhatsApp</p>
                <p className="text-xs text-charcoal/60 mt-1" data-testid="admin-whatsapp-status">
                  {overview.integrations.whatsapp?.enabled
                    ? `Twilio WhatsApp on · templates: ${overview.integrations.whatsapp.templates.join(", ") || "none yet"}`
                    : "Waiting on Twilio upgrade + approved templates — customers can already opt in at checkout"}
                </p>
              </div>
            </div>
            <h2 className="font-display font-semibold text-xl mt-8">Recent admin sign-ins</h2>
            <ActivityLog />
            <h2 className="font-display font-semibold text-xl mt-8">Setup checklist</h2>
            <ul className="mt-3 space-y-2">
              {overview.checklist.map((c, i) => (
                <li key={i} className="bg-white rounded-2xl card-outline p-4 flex gap-3" data-testid={`checklist-${i}`}>
                  <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-1 rounded-full h-fit ${c.status === "live" ? "bg-sage text-forest" : c.status === "test" ? "bg-butter" : "bg-blush text-coral"}`}>{c.status}</span>
                  <div><p className="font-semibold">{c.item}</p><p className="text-xs text-charcoal/60 mt-0.5">{c.note}</p></div>
                </li>
              ))}
            </ul>
            {overview.low_stock.length > 0 && (
              <>
                <h2 className="font-display font-semibold text-xl mt-8 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-coral" /> Low stock</h2>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                  {overview.low_stock.map((l, i) => <li key={i} className="bg-white rounded-2xl card-outline p-3.5 text-xs"><span className="font-semibold">{l.name}</span> — {l.variant}: {l.stock} left</li>)}
                </ul>
              </>
            )}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products" className="space-y-3">
            {products.map((p) => (
              <details key={p.slug} className="bg-white rounded-2xl card-outline p-4">
                <summary className="font-semibold cursor-pointer">{p.name} <span className="text-xs text-charcoal/50 font-normal">· {p.category} · {p.active ? "active" : "hidden"}</span></summary>
                <div className="mt-4 space-y-3">
                  {p.variants.map((v) => (
                    <div key={v.id} className="flex flex-wrap gap-2 items-center text-xs">
                      <span className="w-28 font-semibold">{v.name}</span>
                      <label>Price ₹<input type="number" defaultValue={v.price} data-testid={`admin-price-${p.slug}-${v.id}`} onBlur={(e) => authedPatch(`/admin/products/${p.slug}`, { variants: p.variants.map((vv) => ({ id: v.id, price: +e.target.value, mrp: v.mrp, stock: v.stock })) }).then(load)} className="w-20 border border-charcoal/15 rounded-lg px-2 py-1" /></label>
                      <label>MRP ₹<input type="number" defaultValue={v.mrp || 0} data-testid={`admin-mrp-${p.slug}-${v.id}`} onBlur={(e) => authedPatch(`/admin/products/${p.slug}`, { variants: p.variants.map((vv) => ({ id: v.id, price: v.price, mrp: +e.target.value, stock: v.stock })) }).then(load)} className="w-20 border border-charcoal/15 rounded-lg px-2 py-1" /></label>
                      <label>Stock<input type="number" defaultValue={v.stock} data-testid={`admin-stock-${p.slug}-${v.id}`} onBlur={(e) => authedPatch(`/admin/products/${p.slug}`, { variants: p.variants.map((vv) => ({ id: v.id, price: v.price, mrp: v.mrp, stock: +e.target.value })) }).then(load)} className="w-16 border border-charcoal/15 rounded-lg px-2 py-1" /></label>
                    </div>
                  ))}
                  <label className="flex items-center gap-2 text-xs font-semibold">
                    <input type="checkbox" defaultChecked={p.active} onChange={(e) => authedPatch(`/admin/products/${p.slug}`, { active: e.target.checked }).then(load)} data-testid={`admin-active-${p.slug}`} />
                    Visible in shop
                  </label>
                  <p className="text-[11px] text-charcoal/50">Edits save when a field loses focus. Photos, names and specs are demonstration data — replace before launch.</p>
                </div>
              </details>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="admin-orders">
            {orderMsg && <p role="status" className="text-xs font-semibold bg-butter rounded-2xl px-4 py-2.5" data-testid="admin-order-msg">{orderMsg}</p>}
            {orders.length === 0 && <p className="text-charcoal/60">No orders yet — they'll appear here the moment checkout completes.</p>}
            {orders.map((o) => (
              <div key={o.order_number} className="bg-white rounded-2xl card-outline p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display font-semibold">{o.order_number}</span>
                  <span className="text-xs text-charcoal/60">{new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} · {formatINR(o.total)} · {o.payment_method}{o.payment_status === "paid" ? " · paid" : ""}</span>
                  <span className="text-xs font-bold uppercase ml-auto">{o.status.replace(/_/g, " ")}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2 items-center text-xs">
                  <select defaultValue={o.status} data-testid={`admin-order-status-${o.order_number}`} onChange={(e) => authedPatch(`/admin/orders/${o.order_number}`, { status: e.target.value }).then(load)} className="border border-charcoal/15 rounded-full px-3 py-1.5" aria-label={`Update status for ${o.order_number}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  {!o.shiprocket_order_id && !o.awb && ["confirmed", "packed"].includes(o.status) && (o.payment_method === "cod" || o.payment_status === "paid") && (
                    <button type="button" data-testid={`admin-ship-${o.order_number}`}
                      onClick={() => act(`/admin/orders/${o.order_number}/ship`, (r) => (r.shiprocket_order_id ? `${o.order_number}: sent to Shiprocket (order ${r.shiprocket_order_id}) — choose the courier there` : `${o.order_number}: test shipment · AWB ${r.awb}`))}
                      className="bg-forest text-white font-semibold rounded-full px-3.5 py-1.5 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {isLiveShipping ? "Send to Shiprocket" : "Create test shipment"}</button>
                  )}
                  {o.shiprocket_push_error && <span className="text-coral font-semibold" data-testid={`admin-push-error-${o.order_number}`}>Shiprocket: {o.shiprocket_push_error}</span>}
                  {!isLiveShipping && (
                    <input defaultValue={o.awb || ""} placeholder="AWB number" data-testid={`admin-awb-${o.order_number}`}
                      onBlur={(e) => e.target.value !== (o.awb || "") && authedPatch(`/admin/orders/${o.order_number}`, { status: o.status, awb: e.target.value, courier: o.courier || "Shipping partner" }).then(load)}
                      className="border border-charcoal/15 rounded-full px-3 py-1.5 w-36" aria-label={`AWB for ${o.order_number}`} />
                  )}
                  <span className="text-charcoal/60">{o.address?.name}, {o.address?.city} — {o.address?.pin}</span>
                </div>
                {o.shiprocket_order_id && <ShiprocketPanel order={o} act={act} />}
              </div>
            ))}
          </div>
        )}

        {tab === "abandoned" && <AbandonedCarts />}

        {tab === "reviews" && (
          <div className="space-y-3" data-testid="admin-reviews">
            {reviews.map((r) => (
              <div key={r.id} className="bg-white rounded-2xl card-outline p-4 flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-xs">{r.name} · {r.rating}★ · {r.product_slug} {r.demo && <span className="bg-butter px-2 py-0.5 rounded-full ml-1">sample</span>}</p>
                  <p className="text-xs text-charcoal/70 mt-1">"{r.title}" — {r.body}</p>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold">
                  <input type="checkbox" defaultChecked={r.approved} onChange={(e) => authedPatch(`/admin/reviews/${r.id}`, { approved: e.target.checked }).then(load)} data-testid={`approve-review-${r.id}`} />
                  Approved
                </label>
              </div>
            ))}
          </div>
        )}

        {tab === "coupons" && settings && (
          <div data-testid="admin-coupons">
            <ul className="space-y-2">
              {coupons.map((c) => (
                <li key={c.code} className="bg-white rounded-2xl card-outline p-4 flex items-center gap-4 text-xs">
                  <span className="font-display font-bold">{c.code}</span>
                  <span>{c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`} · min ₹{c.min_order}{c.max_discount ? ` · cap ₹${c.max_discount}` : ""}</span>
                  {c.demo && <span className="bg-butter px-2 py-0.5 rounded-full">demo</span>}
                  <span className="ml-auto font-semibold">{c.active ? "active" : "inactive"}</span>
                </li>
              ))}
            </ul>
            <form onSubmit={(e) => { e.preventDefault(); const f = new FormData(e.target); authedPost("/admin/coupons", { code: f.get("code"), type: f.get("type"), value: +f.get("value"), min_order: +f.get("min_order") }).then(() => load()); e.target.reset(); }}
              className="mt-4 bg-white rounded-2xl card-outline p-4 flex flex-wrap gap-2 items-end" data-testid="admin-coupon-form">
              <label className="text-xs">Code<input name="code" required className="block border border-charcoal/15 rounded-lg px-3 py-1.5 mt-1" /></label>
              <label className="text-xs">Type<select name="type" className="block border border-charcoal/15 rounded-lg px-3 py-1.5 mt-1"><option value="percent">percent</option><option value="flat">flat</option></select></label>
              <label className="text-xs">Value<input name="value" type="number" required className="block border border-charcoal/15 rounded-lg px-3 py-1.5 mt-1 w-20" /></label>
              <label className="text-xs">Min order ₹<input name="min_order" type="number" defaultValue={0} className="block border border-charcoal/15 rounded-lg px-3 py-1.5 mt-1 w-20" /></label>
              <button type="submit" className="bg-forest text-white font-semibold px-4 py-2 rounded-full text-xs">Create coupon</button>
            </form>
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="max-w-2xl space-y-4" data-testid="admin-settings">
            <div className="bg-white rounded-2xl card-outline p-4">
              <label className="text-xs font-bold uppercase tracking-widest text-charcoal/60">Announcement strip</label>
              <input defaultValue={settings.announcement} data-testid="admin-announcement-input"
                onBlur={(e) => authedPut("/admin/settings", { ...settings, announcement: e.target.value }).then(setSettings)}
                className="mt-2 w-full border border-charcoal/15 rounded-2xl px-4 py-3 text-sm" />
              <p className="text-[11px] text-charcoal/50 mt-1">Saves on blur. Shown to every shopper at the top of the site.</p>
            </div>
            <div className="bg-white rounded-2xl card-outline p-4 grid sm:grid-cols-3 gap-3">
              {[["prepaid_flat", "Prepaid shipping ₹"], ["cod_flat", "COD shipping ₹"], ["free_above", "Free shipping above ₹"]].map(([key, label]) => (
                <label key={key} className="text-xs font-semibold">{label}
                  <input type="number" defaultValue={settings.shipping[key]} data-testid={`admin-shipping-${key}`}
                    onBlur={(e) => authedPut("/admin/settings", { ...settings, shipping: { ...settings.shipping, [key]: +e.target.value } }).then(setSettings)}
                    className="block border border-charcoal/15 rounded-lg px-3 py-2 mt-1 w-full" />
                </label>
              ))}
              <p className="sm:col-span-3 text-[11px] text-charcoal/50">Live shipping rules — applied at checkout; delivery estimates and COD availability come from Shiprocket serviceability.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
